/**
 * AI Tokens Consumer Portal — Token Service (Live API)
 *
 * All token operations are routed through the consumer API:
 *   /v1/consumer/packages    – list token packages
 *   /v1/consumer/balance     – current balance + user token
 *   /v1/consumer/purchase    – purchase tokens (credits immediately, no payment yet)
 *   /v1/consumer/transactions – transaction history
 *   /v1/consumer/usage-summary – aggregated usage stats
 *   /v1/consumer/usage-by-app – per-app usage breakdown
 *   /v1/consumer/settings    – user preferences
 */

import {
  TokenBalance,
  TokenPackage,
  TokenRotationResult,
  Transaction,
  UsageSummary,
  UsageByApp,
  UserSettings,
  PurchaseRequest,
  PurchaseResult,
  ApiResponse,
} from '../types';
import { api } from './api.client';

// ─── Token Packages ──────────────────────────────────────────

export async function getTokenPackages(): Promise<ApiResponse<TokenPackage[]>> {
  try {
    const packages = await api.get<TokenPackage[]>('/consumer/packages');
    return { success: true, data: packages };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Balance ─────────────────────────────────────────────────

export async function getTokenBalance(): Promise<ApiResponse<TokenBalance>> {
  try {
    const balance = await api.get<TokenBalance>('/consumer/balance');
    return { success: true, data: balance };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Purchase ────────────────────────────────────────────────

export async function purchaseTokens(
  request: PurchaseRequest
): Promise<ApiResponse<PurchaseResult>> {
  try {
    const result = await api.post<PurchaseResult>('/consumer/purchase', {
      packageId: request.packageId,
    });
    return { success: true, data: result };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Transactions ────────────────────────────────────────────

export async function getTransactions(): Promise<ApiResponse<Transaction[]>> {
  try {
    const transactions = await api.get<Transaction[]>('/consumer/transactions');
    return { success: true, data: transactions };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Usage ───────────────────────────────────────────────────

export async function getUsageSummary(): Promise<ApiResponse<UsageSummary>> {
  try {
    const summary = await api.get<UsageSummary>('/consumer/usage-summary');
    return { success: true, data: summary };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

export async function getUsageByApp(): Promise<ApiResponse<UsageByApp[]>> {
  try {
    const apps = await api.get<UsageByApp[]>('/consumer/usage-by-app');
    return { success: true, data: apps };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Settings ────────────────────────────────────────────────

export async function getUserSettings(): Promise<ApiResponse<UserSettings>> {
  try {
    const settings = await api.get<UserSettings>('/consumer/settings');
    return { success: true, data: settings };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

export async function updateUserSettings(
  settings: Partial<UserSettings>
): Promise<ApiResponse<UserSettings>> {
  try {
    const updated = await api.patch<UserSettings>('/consumer/settings', settings);
    return { success: true, data: updated, message: 'Settings updated successfully.' };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Connect Token ──────────────────────────────────────────

export async function getConnectToken(
  clientId: string
): Promise<ApiResponse<{ token?: string; existingTokenPrefix?: string; isNewToken: boolean }>> {
  try {
    const result = await api.post<{ token?: string; existingTokenPrefix?: string; isNewToken: boolean }>(
      '/consumer/connect-token',
      { clientId }
    );
    return { success: true, data: result };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Token Rotation ──────────────────────────────────────────

export async function rotateToken(): Promise<ApiResponse<TokenRotationResult>> {
  try {
    const result = await api.post<TokenRotationResult>('/consumer/token/rotate', {});
    return { success: true, data: result };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

export async function revokeGraceToken(): Promise<ApiResponse<{ revoked: boolean }>> {
  try {
    const result = await api.post<{ revoked: boolean }>('/consumer/token/revoke-grace', {});
    return { success: true, data: result };
  } catch (err) {
    return { success: false, error: extractError(err) };
  }
}

// ─── Helpers ─────────────────────────────────────────────────

function extractError(err: unknown): string {
  if (err instanceof Error) return err.message;
  if (typeof err === 'string') return err;
  if (err && typeof err === 'object' && 'message' in err) {
    const maybeMessage = (err as { message?: unknown }).message;
    if (typeof maybeMessage === 'string' && maybeMessage.trim().length > 0) {
      return maybeMessage;
    }
  }
  return 'An unexpected error occurred.';
}
